import type { Request } from '@/types/timeline'

import { DAY_MINUTES, clamp } from './time'

export type DragAxis = 'horizontal' | 'vertical'
export type TimelineRequestType = Request['type']

export interface ResizeDurationBounds {
  minDuration: number
  maxDuration: number
}

const VERTICAL_BIAS_MULTIPLIER = 2

export const determineDragAxis = (deltaX: number, deltaY: number): DragAxis =>
  Math.abs(deltaY) > Math.abs(deltaX) * VERTICAL_BIAS_MULTIPLIER ? 'vertical' : 'horizontal'

const normalizeWindow = (start: number, end: number): { start: number; end: number } => {
  const normalizedStart = clamp(Math.round(start), 0, DAY_MINUTES - 1)
  const normalizedEnd = clamp(Math.round(end), normalizedStart + 1, DAY_MINUTES)
  return {
    start: normalizedStart,
    end: normalizedEnd,
  }
}

const normalizeDurationBounds = (bounds?: ResizeDurationBounds): ResizeDurationBounds => {
  if (!bounds) {
    return {
      minDuration: 1,
      maxDuration: DAY_MINUTES,
    }
  }

  const minDuration = Math.max(1, Math.round(bounds.minDuration))
  const maxDuration = Math.max(minDuration, Math.round(bounds.maxDuration))

  return { minDuration, maxDuration }
}

export const clampMovedWindow = (
  rawStart: number,
  duration: number,
  anchorMinutes: number
): { start: number; end: number } => {
  const boundedDuration = Math.max(1, Math.round(duration))
  const minStart = Math.max(0, anchorMinutes + 1 - boundedDuration)
  const maxStart = Math.min(anchorMinutes - 1, DAY_MINUTES - boundedDuration)

  const clampedStart = clamp(Math.round(rawStart), minStart, maxStart)
  return {
    start: clampedStart,
    end: clampedStart + boundedDuration,
  }
}

export const clampResizedStart = (
  rawStart: number,
  end: number,
  anchorMinutes: number,
  bounds?: ResizeDurationBounds,
  shiftStart?: number | null
): number => {
  const { minDuration, maxDuration } = normalizeDurationBounds(bounds)
  const { end: normalizedEnd } = normalizeWindow(rawStart, end)
  const minStart = Math.max(shiftStart ?? 0, normalizedEnd - maxDuration)
  const maxStart = Math.min(normalizedEnd - minDuration, anchorMinutes)

  if (minStart > maxStart) {
    return maxStart
  }

  return clamp(Math.round(rawStart), minStart, maxStart)
}

export const clampResizedEnd = (
  start: number,
  rawEnd: number,
  anchorMinutes: number,
  bounds?: ResizeDurationBounds,
  shiftEnd?: number | null,
  requestType: TimelineRequestType = 'ARR'
): number => {
  const { minDuration, maxDuration } = normalizeDurationBounds(bounds)
  const { start: normalizedStart } = normalizeWindow(start, rawEnd)
  const minEnd =
    requestType === 'DEP'
      ? normalizedStart + minDuration
      : Math.max(normalizedStart + minDuration, anchorMinutes)
  const maxEnd = Math.min(normalizedStart + maxDuration, shiftEnd ?? DAY_MINUTES)

  if (minEnd > maxEnd) {
    return minEnd
  }

  return clamp(Math.round(rawEnd), minEnd, maxEnd)
}
